/**
 * Production Error Boundary with Comprehensive Error Handling
 * 
 * Features:
 * - Graceful error recovery
 * - Error reporting to monitoring systems
 * - User-friendly error messages
 * - Retry mechanisms
 * - Error classification and routing
 */

import React, { Component, ErrorInfo, ReactNode, useState, useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import { Button, Container, Paper, Text, Title, Stack, Group } from '@mantine/core';
// ID generation removed - backend generates all IDs per single execution path principle

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  errorId: string;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, retry: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo, errorId: string) => void;
  maxRetries?: number;
  enableReporting?: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      errorId: ''
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Use timestamp-based ID for error tracking (not for persistence)
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    return {
      hasError: true,
      error,
      errorId
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { onError, enableReporting = true } = this.props;
    const { errorId } = this.state;

    this.setState({ errorInfo });

    // Report error to monitoring systems
    if (enableReporting) {
      this.reportError(error, errorInfo, errorId);
    }

    // Call custom error handler
    if (onError) {
      onError(error, errorInfo, errorId);
    }

    // Show user notification
    this.showErrorNotification(error);

    // Log detailed error information
    console.error('Error Boundary caught an error:', {
      error,
      errorInfo,
      errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });
  }

  private reportError = async (error: Error, errorInfo: ErrorInfo, errorId: string): Promise<void> => {
    try {
      // In production, replace with actual error reporting service
      const errorReport = {
        errorId,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: this.getUserId(),
        tenantId: this.getTenantId(),
        buildVersion: process.env.REACT_APP_VERSION || 'unknown',
        environment: process.env.NODE_ENV
      };

      // ✅ REPOSITORY PATTERN: Use ErrorReportingRepository instead of direct API calls
      const { ErrorReportingRepository, MockErrorReportingRepository } = await import('../data/repositories/ErrorReportingRepository');
      type LifeCriticalError = import('../data/repositories/ErrorReportingRepository').LifeCriticalError;

      // Use mock repository in development
      const isDevelopment = process.env.NODE_ENV === 'development';
      const errorRepo = isDevelopment ? new MockErrorReportingRepository() : new ErrorReportingRepository();

      // Create life-critical error report following FERP data-driven architecture
      const lifeCriticalError: LifeCriticalError = {
        id: errorId,
        type: 'SYSTEM_CRITICAL',
        severity: 'HIGH',
        message: error.message,
        stack: error.stack,
        context: {
          componentStack: errorInfo.componentStack,
          environment: process.env.NODE_ENV,
          buildVersion: errorReport.buildVersion,
          url: errorReport.url
        },
        timestamp: Date.now(),
        tenantId: errorReport.tenantId,
        userId: errorReport.userId,
        sessionId: errorId, // Use errorId as session identifier
        environment: process.env.NODE_ENV || 'production',
        workflowContext: undefined, // Not in a workflow context
        safetyMetadata: {
          affectsPatientSafety: false,
          affectsFinancialIntegrity: false,
          affectsLegalPrivilege: false,
          requiresEmergencyResponse: false,
          estimatedImpact: 'LOW'
        }
      };

      // Report through repository pattern using correct method
      await errorRepo.reportLifeCriticalError(lifeCriticalError);
    } catch (reportError) {
      // Silently handle error reporting failures
      console.warn('Failed to report error:', reportError);
    }
  };

  private showErrorNotification = (error: Error): void => {
    const errorType = this.classifyError(error);
    
    const config = this.getNotificationConfig(errorType);
    
    notifications.show({
      id: this.state.errorId,
      title: config.title,
      message: config.message,
      color: 'red',
      icon: <IconAlertTriangle />,
      autoClose: false,
      withCloseButton: true,
      onClose: () => {
        // Optional: Track notification dismissal
      }
    });
  };

  private classifyError = (error: Error): 'network' | 'validation' | 'authentication' | 'authorization' | 'system' | 'unknown' => {
    const message = error.message.toLowerCase();
    const stack = error.stack?.toLowerCase() || '';

    if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
      return 'network';
    }
    
    if (message.includes('validation') || message.includes('invalid')) {
      return 'validation';
    }
    
    if (message.includes('auth') || message.includes('token') || message.includes('login')) {
      return 'authentication';
    }
    
    if (message.includes('permission') || message.includes('forbidden') || message.includes('unauthorized')) {
      return 'authorization';
    }
    
    if (stack.includes('graphql') || stack.includes('apollo')) {
      return 'network';
    }
    
    return 'system';
  };

  private getNotificationConfig = (errorType: string) => {
    const configs = {
      network: {
        title: 'Connection Error',
        message: 'Unable to connect to server. Please check your internet connection and try again.'
      },
      validation: {
        title: 'Data Validation Error',
        message: 'The data provided is invalid. Please check your input and try again.'
      },
      authentication: {
        title: 'Authentication Error',
        message: 'Your session has expired. Please log in again.'
      },
      authorization: {
        title: 'Access Denied',
        message: 'You don\'t have permission to perform this action.'
      },
      system: {
        title: 'System Error',
        message: 'An unexpected error occurred. Our team has been notified.'
      },
      unknown: {
        title: 'Unexpected Error',
        message: 'Something went wrong. Please try refreshing the page.'
      }
    };

    return configs[errorType as keyof typeof configs] || configs.unknown;
  };

  private handleRetry = (): void => {
    const { maxRetries = 3 } = this.props;
    
    if (this.state.retryCount < maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1
      }));
      
      // Clear error notification
      notifications.hide(this.state.errorId);
      
      // Show retry notification
      notifications.show({
        title: 'Retrying...',
        message: `Attempt ${this.state.retryCount + 1} of ${maxRetries}`,
        color: 'blue',
        autoClose: 2000
      });
    }
  };

  private handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      errorId: ''
    });
    
    notifications.hide(this.state.errorId);
  };

  private getUserId = (): string => {
    // Get user ID from auth context or localStorage
    try {
      const user = JSON.parse(localStorage.getItem('auth_user') || '{}');
      return user.id || 'anonymous';
    } catch {
      return 'anonymous';
    }
  };

  private getTenantId = (): string => {
    // Get tenant ID from auth context or localStorage
    try {
      const user = JSON.parse(localStorage.getItem('auth_user') || '{}');
      return user.tenantId || 'unknown';
    } catch {
      return 'unknown';
    }
  };

  private getAuthToken = (): string => {
    return localStorage.getItem('auth_token') || '';
  };

  componentWillUnmount(): void {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  render(): ReactNode {
    const { hasError, error, retryCount } = this.state;
    const { children, fallback, maxRetries = 3 } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback(error, this.handleRetry);
      }

      // Default error UI
      return (
        <Container size="sm" py="xl">
          <Paper shadow="md" radius="md" p="xl" withBorder>
            <Stack ta="center" gap="lg">
              <IconAlertTriangle size={64} color="var(--mantine-color-red-6)" />
              
              <Stack ta="center" gap="md">
                <Title order={2} ta="center">Something went wrong</Title>
                <Text c="dimmed" ta="center">
                  We&apos;re sorry, but an unexpected error occurred. Our team has been automatically notified.
                </Text>
              </Stack>

              <Group gap="md">
                {retryCount < maxRetries && (
                  <Button
                    leftSection={<IconRefresh size={16} />}
                    onClick={this.handleRetry}
                    variant="filled"
                  >
                    Try Again ({retryCount}/{maxRetries})
                  </Button>
                )}
                
                <Button
                  onClick={this.handleReset}
                  variant="outline"
                >
                  Reset
                </Button>
                
                <Button
                  onClick={() => window.location.reload()}
                  variant="light"
                >
                  Refresh Page
                </Button>
              </Group>

              {process.env.NODE_ENV === 'development' && (
                <Paper p="md" bg="gray.1" radius="sm" style={{ marginTop: 'var(--mantine-spacing-lg)' }}>
                  <Text size="sm" ff="monospace" c="red">
                    <strong>Error:</strong> {error.message}
                  </Text>
                  {error.stack && (
                    <Text size="xs" ff="monospace" c="gray.7" mt="xs">
                      <strong>Stack:</strong>
                      <br />
                      {error.stack}
                    </Text>
                  )}
                </Paper>
              )}
            </Stack>
          </Paper>
        </Container>
      );
    }

    return children;
  }
}

// Higher-order component for easier usage
export const withErrorBoundary = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) => {
  const WithErrorBoundaryComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundaryComponent.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;
  
  return WithErrorBoundaryComponent;
};

// Hook for throwing errors that will be caught by the boundary
export const useErrorHandler = () => {
  const [, setState] = useState();

  return useCallback((error: Error) => {
    setState(() => {
      throw error;
    });
  }, []);
};